import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Heart, Award, MapPin, Phone, Mail } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-rose-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/"
            className="inline-flex items-center space-x-2 text-white/90 hover:text-white transition-colors duration-300 group mb-6"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-300" />
            <span>Back to Home</span>
          </Link>
          
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_suzuki-2026-refresh/artifacts/hdysr3f5_ayumi1%20logo.jpg"
              alt="Ayumi Suzuki Logo"
              className="w-80 h-auto"
            />
          </div>
          
          <h1 className="text-5xl md:text-6xl font-serif mb-4 text-center">About Ayumi Suzuki</h1>
          <p className="text-xl text-teal-100 text-center">Certified Massage Therapist • Midtown Sacramento</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Professional Photo of Ayumi */}
        <div className="flex justify-center mb-12 -mt-32">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-rose-400 rounded-full blur-2xl opacity-20"></div>
            <img 
              src="https://customer-assets.emergentagent.com/job_suzuki-2026-refresh/artifacts/aautielc_Ayumi.jpg"
              alt="Ayumi Suzuki - Certified Massage Therapist"
              className="relative w-64 h-64 object-cover rounded-full border-8 border-white shadow-2xl"
            />
          </div>
        </div>

        {/* Introduction */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-serif text-gray-800 mb-4">Meet Ayumi Suzuki</h2>
          <p className="text-xl text-gray-600 italic">Your Partner in Healing & Wellness</p>
        </div>

        {/* Story Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-12">
          <div className="flex items-center mb-6">
            <Heart className="w-8 h-8 text-rose-500 mr-3" />
            <h2 className="text-3xl font-serif text-gray-800">A Journey of Healing Touch</h2>
          </div>
          
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-6">
            <p className="text-xl text-gray-800 font-medium italic">
              "My journey into massage therapy began with something simple and profound—kneading my mother's shoulders as a child."
            </p>
            
            <p>
              Those early moments of providing comfort and relief to someone I loved planted the seeds for what would become my life's calling. I discovered that the healing power of touch goes far beyond physical relief—it creates connection, brings peace, and offers a moment of restoration in our busy lives.
            </p>
            
            <p>
              Today, as a certified massage therapist practicing in the heart of Midtown Sacramento, I bring that same care and intention to every client who walks through my door. My philosophy is rooted in the belief that helping others through massage provides not only physical relief but also deep personal satisfaction—for both the giver and receiver.
            </p>
            
            <p>
              Each session is personalized to your unique needs. Whether you're seeking relief from chronic tension, recovery from an injury, or simply a moment of tranquility in your day, I approach every treatment with the same dedication and attention I first learned all those years ago.
            </p>
            
            <p className="text-lg text-teal-700 font-medium">
              At my practice on I Street, you'll find a welcoming space designed for your comfort and healing—a place where the ancient art of therapeutic touch meets modern wellness practices.
            </p>
          </div>
        </div>

        {/* Credentials & Approach */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="bg-gradient-to-br from-teal-50 to-white rounded-2xl shadow-lg p-8">
            <Award className="w-10 h-10 text-teal-600 mb-4" />
            <h3 className="text-2xl font-serif text-gray-800 mb-4">Professional Credentials</h3>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-teal-600 mr-2">✓</span>
                <span>Certified Massage Therapist</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-2">✓</span>
                <span>Specialized in Therapeutic Massage</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-2">✓</span>
                <span>Deep Tissue & Swedish Massage Expert</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-2">✓</span>
                <span>Licensed in California</span>
              </li>
            </ul>
          </div>

          <div className="bg-gradient-to-br from-rose-50 to-white rounded-2xl shadow-lg p-8">
            <Heart className="w-10 h-10 text-rose-600 mb-4" />
            <h3 className="text-2xl font-serif text-gray-800 mb-4">My Approach</h3>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-rose-600 mr-2">•</span>
                <span>Personalized treatment plans</span>
              </li>
              <li className="flex items-start">
                <span className="text-rose-600 mr-2">•</span>
                <span>Focus on your specific needs</span>
              </li>
              <li className="flex items-start">
                <span className="text-rose-600 mr-2">•</span>
                <span>Holistic wellness perspective</span>
              </li>
              <li className="flex items-start">
                <span className="text-rose-600 mr-2">•</span>
                <span>Comfortable, welcoming environment</span>
              </li>
              <li className="flex items-start">
                <span className="text-rose-600 mr-2">•</span>
                <span>Commitment to your healing journey</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Philosophy Quote */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl shadow-2xl p-12 text-center text-white mb-12">
          <blockquote className="text-2xl md:text-3xl font-serif italic leading-relaxed">
            "Helping others through massage provides deep personal satisfaction and physical relief. Every session is an opportunity to bring comfort, healing, and peace."
          </blockquote>
          <p className="mt-6 text-teal-100 text-lg">— Ayumi Suzuki</p>
        </div>

        {/* Location & Contact */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <h2 className="text-3xl font-serif text-gray-800 mb-8 text-center">Visit My Practice</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <MapPin className="w-8 h-8 text-teal-600 mx-auto mb-3" />
              <h3 className="font-medium text-gray-800 mb-2">Location</h3>
              <p className="text-gray-600">2528 I Street</p>
              <p className="text-gray-600">Sacramento, CA 95816</p>
              <p className="text-sm text-teal-600 mt-2">Midtown Sacramento</p>
            </div>

            <div className="text-center">
              <Phone className="w-8 h-8 text-teal-600 mx-auto mb-3" />
              <h3 className="font-medium text-gray-800 mb-2">Text Message</h3>
              <a href="sms:4156789133" className="text-teal-600 hover:text-teal-700 font-medium">
                415-678-9133
              </a>
              <p className="text-sm text-gray-500 mt-1">Tap to text</p>
            </div>

            <div className="text-center">
              <Mail className="w-8 h-8 text-teal-600 mx-auto mb-3" />
              <h3 className="font-medium text-gray-800 mb-2">Email</h3>
              <a href="mailto:ayumisuzuki@yahoo.com" className="text-teal-600 hover:text-teal-700 font-medium break-all">
                ayumisuzuki@yahoo.com
              </a>
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/book"
              className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-10 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:scale-105 shadow-lg"
            >
              Book Your Appointment
            </Link>
          </div>
        </div>

        {/* Services Link */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">Explore our full range of services</p>
          <Link
            to="/services-menu"
            className="text-teal-600 hover:text-teal-700 font-medium hover:underline"
          >
            View Services Menu →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
